// Lets staff change a vehicle's fleet number themselves, safely -- fleet
// number is the primary key referenced by ~13 other tables (shift history,
// TaxiCaller jobs, inspections, fuel cards, maintenance records...), so a
// plain edit isn't possible. Calls the rename_vehicle_number() Postgres
// function (see supabase/migrations), which moves every referencing row
// across in one atomic transaction -- nothing is deleted or recreated, the
// vehicle's full history just follows it to the new number.
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { ArrowRight } from "lucide-react";

interface RenumberVehicleModalProps {
  vehicleNumber: string;
  open: boolean;
  onClose: () => void;
  onRenumbered: () => void;
}

const RenumberVehicleModal: React.FC<RenumberVehicleModalProps> = ({
  vehicleNumber,
  open,
  onClose,
  onRenumbered,
}) => {
  const [newNumber, setNewNumber] = useState("");
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    setNewNumber("");
    onClose();
  };

  const handleConfirm = async () => {
    const trimmed = newNumber.trim();
    if (!trimmed) {
      toast({ title: "Enter a new fleet number", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.rpc("rename_vehicle_number", {
      old_number: vehicleNumber,
      new_number: trimmed,
    });
    setSaving(false);

    if (error) {
      toast({ title: "Couldn't renumber vehicle", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: "Vehicle renumbered", description: `${vehicleNumber} is now ${trimmed} -- full history moved with it.` });
    setNewNumber("");
    onRenumbered();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle>Renumber Vehicle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Changes this vehicle's fleet number everywhere it's used -- shift history, TaxiCaller jobs, inspections,
            fuel cards, maintenance records and more all move with it. Nothing is deleted or recreated.
          </p>

          <div className="flex items-center justify-center gap-3">
            <div className="text-center">
              <Label className="text-xs text-muted-foreground">Current</Label>
              <div className="text-lg font-semibold golden-text">{vehicleNumber}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground mt-4" />
            <div className="text-center flex-1 max-w-[140px]">
              <Label htmlFor="new-fleet-number" className="text-xs text-muted-foreground">
                New
              </Label>
              <Input
                id="new-fleet-number"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                placeholder="e.g. 442"
                autoFocus
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={saving || !newNumber.trim()} className="golden-button">
            {saving ? "Renumbering..." : "Renumber"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenumberVehicleModal;
